import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Scan, FolderSearch, FileSignature, ClipboardCheck, CheckCircle2, ArrowRight, CreditCard, Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const STEPS = [
  { key: "laser_scanner", n: 1, icon: Scan, testid: "step-laser-scanner-card", title: "Rilievo Laser Scanner 3D", desc: "Rilievo strumentale dello stato dei luoghi con restituzione vettoriale accurata." },
  { key: "accesso_atti", n: 2, icon: FolderSearch, testid: "step-accesso-atti-card", title: "Accesso agli Atti", desc: "Recupero atti probatori presso l'archivio edilizia privata del Comune di riferimento." },
  { key: "report_asseverato", n: 3, icon: FileSignature, testid: "step-report-asseverato-card", title: "Report Asseverato", desc: "Sovrapposizioni con report informativo professionale sui risultati della due diligence." },
  { key: "audit_urbanistico", n: 4, icon: ClipboardCheck, testid: "step-audit-urbanistico-card", title: "Audit Tecnico-Urbanistico", desc: "Controllo di conformità completo per la piena legittimità dello stato di diritto." },
];

const eur = (v) => Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export const SpecialistServices = ({ pratica }) => {
  const qc = useQueryClient();
  const services = pratica.services || [];
  const byKey = Object.fromEntries(services.map((s) => [s.service_key, s]));

  const { data: prices } = useQuery({ queryKey: ["service-prices"], queryFn: async () => (await api.get("/payments/prices")).data, staleTime: 600000 });

  const requestMut = useMutation({
    mutationFn: async (service_key) => (await api.post(`/pratiche/${pratica.id}/services`, { service_key })).data,
    onSuccess: (s) => { qc.invalidateQueries({ queryKey: ["pratica", pratica.id] }); toast.success(`Preventivo richiesto: ${s.nome}`); },
    onError: () => toast.error("Errore nella richiesta"),
  });
  const payMut = useMutation({
    mutationFn: async (service_key) => (await api.post("/payments/checkout", { pratica_id: pratica.id, service_key, origin_url: window.location.origin })).data,
    onSuccess: ({ checkout_url }) => { window.location.href = checkout_url; },
    onError: (e) => toast.error(e.response?.data?.detail || "Impossibile avviare il pagamento"),
  });

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.03] p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
          <ArrowRight className="h-5 w-5 text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-lg">Servizi Specialistici a Step Progressivi</h3>
          <p className="text-sm text-slate-400 mt-1">
            Esito non conforme: attiva la verifica approfondita per step, così da contenere i costi di consulenza. Pagamento sicuro con Stripe, IVA calcolata al checkout.
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5" /> Stripe</span>
      </div>

      <div className="space-y-3">
        {STEPS.map((s) => {
          const sv = byKey[s.key];
          const paid = sv?.stato === "pagato";
          const price = prices?.[s.key];
          const paying = payMut.isPending && payMut.variables === s.key;
          return (
            <div key={s.key} data-testid={s.testid} className={`flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-4 rounded-lg bg-[#0B0F17] border ${paid ? "border-emerald-500/40" : "border-slate-800"}`}>
              <div className="h-9 w-9 rounded-full bg-[#1A2332] border border-slate-700 flex items-center justify-center shrink-0 font-mono-t text-sm text-cyan-400">{s.n}</div>
              <s.icon className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                {price && <p data-testid={`service-price-${s.key}`} className="font-mono-t text-xs text-cyan-300 mt-1">{eur(price.amount)} <span className="text-slate-500">+ IVA</span></p>}
              </div>
              {paid ? (
                <span data-testid={`service-paid-${s.key}`} className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-medium shrink-0">
                  <CheckCircle2 className="h-4 w-4" /> Pagato {sv.pagamento?.importo ? `· ${eur(sv.pagamento.importo)}` : ""}
                </span>
              ) : (
                <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                  {sv ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-300 text-xs font-medium"><CheckCircle2 className="h-4 w-4" /> Richiesto</span>
                  ) : (
                    <button data-testid={`request-service-${s.key}`} onClick={() => requestMut.mutate(s.key)} disabled={requestMut.isPending}
                      className="px-3 py-2 rounded-lg bg-[#1A2332] border border-slate-700 text-xs hover:border-amber-500/50 disabled:opacity-50">Richiedi preventivo</button>
                  )}
                  <button data-testid={`pay-service-${s.key}`} onClick={() => payMut.mutate(s.key)} disabled={payMut.isPending || !price}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/90 text-slate-900 text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50">
                    {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Paga ora
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
